<template>
  <Grid
    class="plant-grid"
    :data-items="plants"
    :columns="columns"
    :loader="plantsLoading"
    selected-field="IsAuthorized"
    @selectionchange="changeSelection"
    @rowclick="changeSelection"
    data-cy="plant-grid"
  >
    <template v-slot:SelectHeaderCell>
      <th>
        <input
          type="checkbox"
          :checked="allPlantsSelected"
          ref="selectAllCheckbox"
          @change="onHeaderSelectionChange"
          :disabled="!editable || includeFuturePlantsDefault"
        />
      </th>
    </template>
    <template v-slot:SelectCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <input
          type="checkbox"
          v-model="dataItem[field]"
          :disabled="!editable || includeFuturePlantsDefault"
        />
      </td>
    </template>
    <template v-slot:SelectHeaderFooter>
      <input
        type="checkbox"
        :checked="includeFuturePlants"
        @change="
          emit(
            'includeFuturePlantsChanged',
            ($event.target as HTMLInputElement).checked
          )
        "
        id="includeFutureCheckbox"
        ref="includeFutureCheckbox"
        :disabled="!editable"
      />
    </template>
    <template v-slot:SelectHeaderTextFooter>
      <label for="includeFutureCheckbox">Include all Future Plants</label>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import {
  Grid,
  type GridColumnProps,
  type GridRowClickEvent,
  type GridSelectionChangeEvent,
} from "@progress/kendo-vue-grid";
import { ref, computed, watchEffect } from "vue";
import type { Facility } from "@/Domain/Facility";

const props = defineProps<{
  plants: Facility[];
  plantsLoading: boolean;
  includeFuturePlantsDefault: boolean;
  editable: boolean;
}>();

const emit = defineEmits<{
  (e: "includeFuturePlantsChanged", includeFuturePlants: boolean): void;
}>();

const includeFuturePlants = ref(props.includeFuturePlantsDefault);

const columns = ref<GridColumnProps[]>([
  {
    field: "IsAuthorized",
    width: "50px",
    sortable: false,
    filterable: false,
    headerCell: "SelectHeaderCell",
    cell: "SelectCell",
    headerClassName: "hide-menu",
    footerCell: "SelectHeaderFooter",
  },
  {
    title: "Plant Name",
    field: "Name",
    footerCell: "SelectHeaderTextFooter",
  },
  {
    title: "Status",
    field: "StatusDisplayName",
    width: "100px",
  },
]);

const allPlantsSelected = computed(() => {
  return props.plants.every((plant) => plant.IsAuthorized);
});

const onHeaderSelectionChange = function (event: Event) {
  const checked = (event.target as HTMLInputElement).checked;
  props.plants.forEach((plant) => {
    plant.IsAuthorized = checked;
  });
};

const changeSelection = function (
  event: GridSelectionChangeEvent | GridRowClickEvent
) {
  if (!props.editable) return;

  const plant = event.dataItem as Facility;
  if (!plant) return;
  plant.IsAuthorized = !plant.IsAuthorized;
};

watchEffect(() => {
  includeFuturePlants.value = props.includeFuturePlantsDefault;
});
</script>
